import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '../StyledTypography';
import Table from 'apollo-react/components/Table';
import IconButton from 'apollo-react/components/IconButton';
import OpenNew from 'apollo-react-icons/OpenNew';
import styles from '../LinkedStudies/LinkedStudies.module.css';

/**
 * Read-only list of the Study Notifications (parent requests) linked to an opened
 * sub-workflow request (applicant or reviewer).
 */
const ParentRequestInfo = ({
  parentRequests = [],
  detailPathPrefix = '/enduser/progress/requests',
}) => {
  const { t } = useTranslation();

  const rows = useMemo(() => {
    return parentRequests.map((parentRequest) => ({
      id: parentRequest.id,
      study_name: parentRequest.study_name,
      generated_id: parentRequest.generated_id,
      workflow_name:
        parentRequest.WorkflowVersion?.workflow?.name ||
        parentRequest.workflow_name ||
        '-',
      date_ended: parentRequest.date_ended,
    }));
  }, [parentRequests]);

  const columns = useMemo(
    () => [
      {
        header: t('Request Name', 'Request Name'),
        accessor: 'study_name',
        customCell: ({ row }) => row.study_name || row.generated_id,
      },
      {
        header: t('ID'),
        accessor: 'generated_id',
      },
      {
        header: t('Workflow', 'Workflow'),
        accessor: 'workflow_name',
      },
      {
        header: t('Approval Date', 'Approval Date'),
        accessor: 'date_ended',
        customCell: ({ row }) =>
          row.date_ended ? new Date(row.date_ended).toLocaleDateString() : '-',
      },
      {
        accessor: 'actions',
        customCell: ({ row }) => {
          const requestName = row.study_name || row.generated_id;
          const handleViewDetails = (e) => {
            e.stopPropagation();
            const url = `${detailPathPrefix}/${row.id}?request_name=${encodeURIComponent(requestName)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
          };
          return (
            <IconButton
              size="small"
              onClick={handleViewDetails}
              title={t('view_details')}
            >
              <OpenNew />
            </IconButton>
          );
        },
        width: 80,
      },
    ],
    [t, detailPathPrefix]
  );

  if (!rows.length) {
    return (
      <div className={styles.emptyContainer}>
        <Typography variant="bodyDefault">
          {t(
            'parentRequest.noneLinked',
            'No study notification is linked to this submission.'
          )}
        </Typography>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Typography variant="bodyDefault">
          {t(
            'parentRequest.linkedPrompt',
            'This submission is linked to the following Approved Study Notifications.'
          )}
        </Typography>
      </div>
      <Table columns={columns} rows={rows} rowId="id" hidePagination />
    </div>
  );
};

export default ParentRequestInfo;
